export interface CalculateBreadcrumbFoldOptions {
	/** Widths of every crumb except the last, in order. These are the ones that can fold into the …. */
	foldableItemWidths: number[];
	/** The last crumb's natural width. Never folded, only released to shrink. */
	lastNaturalWidth: number;
	containerWidth: number;
	ellipsisWidth: number;
	/** Floor the last crumb shrinks to once released. */
	lastMinWidth: number;
	/** Separator element width (breadcrumb flex gap is 0). */
	gap: number;
}

export interface CalculateBreadcrumbFoldResult {
	/** Crumbs folded into the … (0 = NOT_FOLDED). Includes the first crumb when firstFolded. */
	foldedCount: number;
	lastReleased: boolean;
	firstFolded: boolean;
}

const NOT_FOLDED: CalculateBreadcrumbFoldResult = { foldedCount: 0, lastReleased: false, firstFolded: false };

/** Laid-out width: sum of widths plus one gap per junction. */
function rowWidth(widths: number[], gap: number): number {
	if (widths.length === 0) return 0;
	const items = widths.reduce((sum, width) => sum + width, 0);
	return items + (widths.length - 1) * gap;
}

/**
 * Shallowest fold state that fits `containerWidth`. Escalates through the phases in order:
 *   A everything fits → B fold middle left→right → C release last → D fold first (leading …).
 * The fit decision always uses the last crumb's natural width, never its shrunk render width.
 */
export function calculateBreadcrumbFold(options: CalculateBreadcrumbFoldOptions): CalculateBreadcrumbFoldResult {
	const { foldableItemWidths, lastNaturalWidth, containerWidth, ellipsisWidth, lastMinWidth, gap } = options;
	const foldableCount = foldableItemWidths.length;

	if (foldableCount === 0) {
		// Only the last crumb exists; it can be released to truncate but never folded away.
		return lastNaturalWidth <= containerWidth ? NOT_FOLDED : { foldedCount: 0, lastReleased: true, firstFolded: false };
	}

	const first = foldableItemWidths[0]!;
	const middleCount = foldableCount - 1;

	// A — everything fits.
	const allWidths = [...foldableItemWidths, lastNaturalWidth];
	if (rowWidth(allWidths, gap) <= containerWidth) return NOT_FOLDED;

	// B — fold middle crumbs left→right, keeping first + last.
	for (let k = 1; k <= middleCount; k++) {
		const tail = foldableItemWidths.slice(k + 1);
		const widths = [first, ellipsisWidth, ...tail, lastNaturalWidth];

		if (rowWidth(widths, gap) <= containerWidth) {
			return { foldedCount: k, lastReleased: false, firstFolded: false };
		}
	}

	// C — all middle folded, release the last crumb to its floor. With no middle crumbs there is no …
	// yet, so the row is just first · last@min.
	const releasedWidths = middleCount > 0 ? [first, ellipsisWidth, lastMinWidth] : [first, lastMinWidth];

	if (rowWidth(releasedWidths, gap) <= containerWidth) {
		return { foldedCount: middleCount, lastReleased: true, firstFolded: false };
	}

	// D — fold the first crumb into a leading … too → … · last@min.
	return { foldedCount: middleCount + 1, lastReleased: true, firstFolded: true };
}
